import {
  ApiBase,
  ApiResponse,
  ExpertisePackageReportResponse,
  QueriesReportAdminResponse,
  QueriesReportResponse,
  SaleReportAdminResponse,
  SaleReportResponse,
  FinancialSummaryReportResponse,
  GetFinancialSummaryReportRequest,
  SalesReportDataResponse,
  QueriesReportDataResponse,
  PackageSalesReportDataResponse,
  AdditionalServiceReportAdminResponse,
  AdditionalServiceReportDataResponse,
  CampaignListReportResponse,
  CampaignUsedDetailReportResponse,
  CheckCampaignResponse,
  UserGroup,
} from 'types';
import { DashboardReportResponse } from 'types/dashboard-report.types';

const BASE_URL = 'reporting';

export class ReportingApi extends ApiBase {
  public async getSalesReport(
    startDateTimestamp: number,
    endDateTimestamp: number,
    externalPackageGroupSource?: number | null,
    onlyDiscountedSales?: boolean,
  ): Promise<ApiResponse<SaleReportResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      externalPackageGroupSource,
      onlyDiscountedSales,
    });

    return response.data;
  }

  public async getSalesReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    externalPackageGroupSource?: number | null,
    onlyDiscountedSales?: boolean,
    groupType?: number,
  ): Promise<ApiResponse<SaleReportAdminResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report-admin`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      externalPackageGroupSource,
      onlyDiscountedSales,
      groupType,
    });

    return response.data;
  }

  public async getSalesReportData(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: number,
  ): Promise<ApiResponse<SalesReportDataResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report-data`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async exportSalesReport(
    startDateTimestamp: number,
    endDateTimestamp: number,
    externalPackageGroupSource?: number | null,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-sales-report`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        externalPackageGroupSource,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async exportSalesReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    externalPackageGroupSource?: number | null,
    onlyDiscountedSales?: boolean,
    groupType?: UserGroup,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-sales-report-admin`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        expertisePointId,
        externalPackageGroupSource,
        onlyDiscountedSales,
        groupType,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async getSalesReportByExpertisePackage(
    startDateTimestamp: number,
    endDateTimestamp: number,
  ): Promise<ApiResponse<ExpertisePackageReportResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report-by-expertise-package`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
    });

    return response.data;
  }

  public async getSalesReportByExpertisePackageAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<ExpertisePackageReportResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report-by-expertise-package-admin`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async exportSalesReportByExpertisePackage(
    startDateTimestamp: number,
    endDateTimestamp: number,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-sales-report-by-expertise-package`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async exportSalesReportByExpertisePackageAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-sales-report-by-expertise-package-admin`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        expertisePointId,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async getSalesReportByExpertisePackageData(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<PackageSalesReportDataResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-sales-report-by-expertise-package-data`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async getQueriesReport(
    startDateTimestamp: number,
    endDateTimestamp: number,
  ): Promise<ApiResponse<QueriesReportResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-queries-report`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
    });

    return response.data;
  }

  public async getQueriesReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<QueriesReportAdminResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-queries-report-admin`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async getQueriesReportData(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<QueriesReportDataResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-queries-report-data`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async exportQueriesReport(startDateTimestamp: number, endDateTimestamp: number): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-queries-report`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async exportQueriesReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-queries-report-admin`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        expertisePointId,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async getFinancialSummaryReport(
    request: GetFinancialSummaryReportRequest,
  ): Promise<ApiResponse<FinancialSummaryReportResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/financial-summary-report`, request);
    return response.data;
  }

  public async exportFinancialSummaryReport(request: GetFinancialSummaryReportRequest): Promise<Blob> {
    const response = await this.apiClient.post(`${BASE_URL}/export-financial-summary-report`, request, {
      responseType: 'blob',
    });
    return response.data;
  }

  public async getAdditionalServiceReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<AdditionalServiceReportAdminResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-additional-service-report-admin`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async exportAdditionalServiceReportAdmin(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<Blob> {
    const response = await this.apiClient.post(
      `${BASE_URL}/export-additional-service-report-admin`,
      {
        startDate: startDateTimestamp,
        endDate: endDateTimestamp,
        expertisePointId,
        groupType,
      },
      { responseType: 'blob' },
    );

    return response.data;
  }

  public async getAdditionalServiceReportData(
    startDateTimestamp: number,
    endDateTimestamp: number,
    expertisePointId?: number,
    groupType?: UserGroup,
  ): Promise<ApiResponse<AdditionalServiceReportDataResponse[]>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-additional-service-report-data`, {
      startDate: startDateTimestamp,
      endDate: endDateTimestamp,
      expertisePointId,
      groupType,
    });

    return response.data;
  }

  public async getDashboardReport(groupType?: number): Promise<ApiResponse<DashboardReportResponse>> {
    const response = await this.apiClient.post(`${BASE_URL}/get-dashboard-report`, { groupType });
    return response.data;
  }

  // Campaign Report Methods
  public async getCampaignListReport(groupType?: number): Promise<ApiResponse<CampaignListReportResponse>> {
    const response = await this.apiClient.get('campaign/list-report', {
      params: groupType ? { groupType } : undefined,
    });
    return response.data;
  }

  public async getCampaignUsedDetailReport(
    campaignId: number,
  ): Promise<ApiResponse<CampaignUsedDetailReportResponse[]>> {
    const response = await this.apiClient.get(`campaign/used-detail-report/${campaignId}`);
    return response.data;
  }

  public async exportCampaignReport(groupType?: number): Promise<Blob> {
    const response = await this.apiClient.post('campaign/export-list-report', { groupType }, { responseType: 'blob' });
    return response.data;
  }

  public async exportCampaignUsedDetailReport(campaignId: number): Promise<Blob> {
    const response = await this.apiClient.get(`campaign/export-used-detail-report/${campaignId}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  public async checkCampaign(phoneNumber: string): Promise<ApiResponse<CheckCampaignResponse>> {
    const response = await this.apiClient.get('campaign/check', {
      params: {
        phoneNumber,
      },
    });
    return response.data;
  }
}
